import { Capacitor } from "@capacitor/core";
import {
  MISSING_NATIVE_API_BASE,
  apiBaseLabel,
  effectiveApiBase,
  resolveApiUrl,
  shouldRequireApiBase,
} from "./api-url";
import {
  isNotFound,
  type AisleGraph,
  type AskAiReply,
  type AskAiRequest,
  type CheckInInput,
  type HallBoothCollection,
  type HealthResult,
  type McpEnvelope,
  type PresenceListResult,
  type PresenceRecord,
  type PresenceUpdateInput,
} from "./types";

export { apiBaseLabel, resolveApiUrl } from "./api-url";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let tokenProvider: () => Promise<string | undefined> = async () => undefined;

/** Client abort; keep above the BFF's MCP timeout so the UI is not stuck forever. */
const CLIENT_FETCH_MS = 45_000;
const ASK_AI_FETCH_MS = 90_000;

type FetchInit = RequestInit & { timeoutMs?: number; timeoutMessage?: string };

export function setApiTokenProvider(provider: () => Promise<string | undefined>): void {
  tokenProvider = provider;
}

async function authHeaders(init?: RequestInit): Promise<Headers> {
  const headers = new Headers(init?.headers);
  const token = await tokenProvider();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

export async function apiFetch(url: string, init: FetchInit = {}): Promise<Response> {
  const { timeoutMs = CLIENT_FETCH_MS, timeoutMessage, signal, ...rest } = init;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
  }
  try {
    const headers = await authHeaders(rest);
    return await fetch(url, { ...rest, headers, signal: controller.signal });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new ApiError(
        timeoutMessage ?? "MCP request timed out. The catalog may be waking up — wait a few seconds and retry.",
        504,
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

function configuredApiBase(): string | undefined {
  return effectiveApiBase(
    import.meta.env.VITE_API_BASE_URL,
    typeof window !== "undefined" ? window.location.origin : undefined,
    Capacitor.isNativePlatform(),
  );
}

/**
 * Same-origin `/api` in the browser (`npm run dev` proxy or BFF-served SPA).
 * On a packaged Capacitor device the WebView origin is https://localhost, so
 * VITE_API_BASE_URL must be the reachable BFF (deployed HTTPS or local HTTP).
 */
export function apiUrl(path: string): string {
  const base = configuredApiBase();
  if (
    shouldRequireApiBase(
      base,
      typeof window !== "undefined" ? window.location.origin : undefined,
      Capacitor.isNativePlatform(),
    )
  ) {
    throw new ApiError(MISSING_NATIVE_API_BASE);
  }
  return resolveApiUrl(path, base);
}

export function configuredApiBaseLabel(): string {
  return apiBaseLabel(configuredApiBase());
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const record = body as { error?: unknown; details?: { message?: unknown } };
    if (typeof record.error === "string" && record.error.trim()) {
      return record.error;
    }
    if (typeof record.details?.message === "string") {
      return record.details.message;
    }
  }
  return fallback;
}

export function unwrapHealthPayload(body: unknown): HealthResult {
  let current = body;
  for (let i = 0; i < 5; i++) {
    if (typeof current === "string") {
      const trimmed = current.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          current = JSON.parse(trimmed) as unknown;
          continue;
        } catch {
          return { status: trimmed };
        }
      }
      return { status: trimmed };
    }
    if (current && typeof current === "object" && !Array.isArray(current) && "result" in current) {
      const inner = (current as McpEnvelope<unknown>).result;
      if (typeof inner === "string" || (inner && typeof inner === "object")) {
        current = inner;
        continue;
      }
    }
    break;
  }
  if (current && typeof current === "object" && !Array.isArray(current)) {
    return current as HealthResult;
  }
  return {};
}

export async function fetchHealth(): Promise<HealthResult> {
  try {
    return await fetchHealthOnce();
  } catch (error) {
    if (error instanceof ApiError && error.status === 504) {
      return await fetchHealthOnce();
    }
    throw error;
  }
}

async function fetchHealthOnce(): Promise<HealthResult> {
  const response = await apiFetch(apiUrl("/api/health"));
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Health check failed (${response.status})`), response.status);
  }
  return unwrapHealthPayload(body);
}

export interface CampaignBuilderHealthResult {
  module: string;
  mcp: { ok: boolean; error?: string };
  sendgrid: { configured: boolean; source: string; error: string | null };
  key_vault: string;
  sendgrid_secret_name: string;
}

export async function fetchCampaignBuilderHealth(): Promise<CampaignBuilderHealthResult> {
  const response = await apiFetch(apiUrl("/api/campaign-builder/health"));
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(
      errorMessage(body, `Campaign Builder health failed (${response.status})`),
      response.status,
    );
  }
  return body as CampaignBuilderHealthResult;
}

export async function callTool<T>(
  name: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  const response = await apiFetch(apiUrl("/api/mcp/call"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, arguments: args }),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `MCP call ${name} failed (${response.status})`), response.status);
  }

  return unwrapMcpCallPayload<T>(body);
}

export async function callPublicTool<T>(
  name: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  const response = await apiFetch(apiUrl("/exhibitor/mcp/call"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, arguments: args }),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `MCP call ${name} failed (${response.status})`), response.status);
  }
  return unwrapMcpCallPayload<T>(body);
}

/** BFF envelope `{ result }`, including when MCP returns a JSON string. */
function unwrapMcpCallPayload<T>(body: unknown): T {
  let current: unknown = body;
  if (current && typeof current === "object" && "result" in current) {
    current = (current as McpEnvelope<unknown>).result;
  }
  if (typeof current === "string") {
    const trimmed = current.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        current = JSON.parse(trimmed) as unknown;
      } catch {
        return current as T;
      }
    }
  }
  return current as T;
}

export async function fetchHallBooths(hall: string): Promise<HallBoothCollection> {
  const response = await apiFetch(apiUrl(`/api/floors/${encodeURIComponent(hall)}`));
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Hall ${hall} geometry failed (${response.status})`), response.status);
  }
  return body as HallBoothCollection;
}

export async function fetchPublicHallBooths(hall: string): Promise<HallBoothCollection> {
  const response = await apiFetch(apiUrl(`/exhibitor/floors/${encodeURIComponent(hall)}`));
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Hall ${hall} geometry failed (${response.status})`), response.status);
  }
  return body as HallBoothCollection;
}

export async function fetchHallAisles(hall: string): Promise<AisleGraph> {
  const response = await apiFetch(apiUrl(`/api/aisles?hall=${encodeURIComponent(hall)}`));
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Hall ${hall} aisles failed (${response.status})`), response.status);
  }
  return body as AisleGraph;
}

export async function fetchPublicHallAisles(hall: string): Promise<AisleGraph> {
  const response = await apiFetch(apiUrl(`/exhibitor/aisles?hall=${encodeURIComponent(hall)}`));
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Hall ${hall} aisles failed (${response.status})`), response.status);
  }
  return body as AisleGraph;
}

export async function postCheckIn(input: CheckInInput): Promise<PresenceRecord> {
  const response = await apiFetch(apiUrl("/api/checkins"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Check-in failed (${response.status})`), response.status);
  }
  const envelope = body as { result?: PresenceRecord } | PresenceRecord;
  if (envelope && typeof envelope === "object" && "result" in envelope && envelope.result) {
    return envelope.result;
  }
  return envelope as PresenceRecord;
}

export async function postPresence(input: PresenceUpdateInput): Promise<PresenceRecord> {
  const response = await apiFetch(apiUrl("/api/presence"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Presence update failed (${response.status})`), response.status);
  }
  const envelope = body as { result?: PresenceRecord } | PresenceRecord;
  if (envelope && typeof envelope === "object" && "result" in envelope && envelope.result) {
    return envelope.result;
  }
  return envelope as PresenceRecord;
}

export async function postAskAi(
  input: AskAiRequest,
  copilotToken?: string,
): Promise<AskAiReply> {
  const payload = copilotToken ? { ...input, copilot_token: copilotToken } : input;
  const response = await apiFetch(apiUrl("/api/ask-ai"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    timeoutMs: ASK_AI_FETCH_MS,
    timeoutMessage: "Ask AI timed out.",
    body: JSON.stringify(payload),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Ask AI failed (${response.status})`), response.status);
  }
  const envelope = body as { result?: AskAiReply } | AskAiReply;
  if (envelope && typeof envelope === "object" && "result" in envelope && envelope.result) {
    return envelope.result;
  }
  return envelope as AskAiReply;
}

export async function fetchPresence(eventKey: string): Promise<PresenceListResult> {
  const response = await apiFetch(apiUrl(`/api/presence?event_key=${encodeURIComponent(eventKey)}`));
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Presence lookup failed (${response.status})`), response.status);
  }
  return body as PresenceListResult;
}

/** wss:// (or ws://) URL for the live presence roster, honoring VITE_API_BASE_URL. */
export function presenceSocketUrl(eventKey: string, accessToken?: string): string {
  const path = apiUrl("/api/presence/stream");
  const url = new URL(path, window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("event_key", eventKey);
  if (accessToken) {
    url.searchParams.set("access_token", accessToken);
  }
  return url.toString();
}

export function describeResultError(result: unknown, fallback = "No matching record"): string {
  if (isNotFound(result)) {
    return "not_found — the MCP catalog has no record for this lookup.";
  }
  if (result && typeof result === "object" && "error" in result) {
    const error = (result as { error?: unknown }).error;
    if (typeof error === "string" && error.trim()) {
      return error;
    }
  }
  return fallback;
}

export type PlusLeadCapture = {
  lead_id?: string;
  profile?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    title?: string;
    company?: string;
    website?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    city?: string;
    state?: string;
    postal?: string;
    country?: string;
    demographics?: Array<{
      question?: string;
      answers?: string[];
    }>;
  };
};

export type CampaignBadgeScanKey = {
  campaign_key: string;
  campaign_name: string;
  marketing_campaign_id: string | null;
  badge_scan_key: string | null;
  locked: boolean;
};

export async function fetchCampaignBadgeScanKey(
  campaignKey: string,
  campaignName?: string,
): Promise<CampaignBadgeScanKey> {
  const params = new URLSearchParams({ campaign_key: campaignKey });
  if (campaignName?.trim()) params.set("campaign_name", campaignName.trim());
  const response = await apiFetch(apiUrl(`/api/campaign-builder/campaign-badge-scan-key?${params.toString()}`));
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Campaign badge key lookup failed (${response.status})`), response.status);
  }
  return body as CampaignBadgeScanKey;
}

export async function saveCampaignBadgeScanKey(
  campaignKey: string,
  badgeScanKey: string,
  campaignName?: string,
): Promise<CampaignBadgeScanKey> {
  const response = await apiFetch(apiUrl("/api/campaign-builder/campaign-badge-scan-key"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      campaign_key: campaignKey,
      campaign_name: campaignName,
      badge_scan_key: badgeScanKey,
    }),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Campaign badge key save failed (${response.status})`), response.status);
  }
  return body as CampaignBadgeScanKey;
}

export async function fetchBadgeLead(barcode: string, campaignKey: string): Promise<PlusLeadCapture> {
  const response = await apiFetch(apiUrl("/api/badge-scan/capture"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ barcode, campaign_key: campaignKey }),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Badge capture failed (${response.status})`), response.status);
  }
  return body as PlusLeadCapture;
}

export async function assignBadgeAlphaCode(contactId: string): Promise<string> {
  const response = await apiFetch(apiUrl("/api/badge-scan/alpha-code"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contact_id: contactId }),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Alpha code assignment failed (${response.status})`), response.status);
  }
  const code = body && typeof body === "object" && "alpha_code" in body ? body.alpha_code : null;
  if (typeof code !== "string" || !/^[A-Z]{4}$/.test(code.trim().toUpperCase())) {
    throw new ApiError("Alpha code assignment did not return a 4-letter code", response.status);
  }
  return code.trim().toUpperCase();
}

export async function fetchBadgeAlphaCode(contactId: string): Promise<string | null> {
  const params = new URLSearchParams({ contact_id: contactId });
  const response = await apiFetch(apiUrl(`/api/badge-scan/alpha-code?${params.toString()}`));
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Alpha code lookup failed (${response.status})`), response.status);
  }
  const code = body && typeof body === "object" && "alpha_code" in body ? body.alpha_code : null;
  return typeof code === "string" && code.trim() ? code.trim() : null;
}

export async function queueBadgeAugmentation(payload: Record<string, unknown>): Promise<void> {
  const response = await apiFetch(apiUrl("/api/badge-scan/augment"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Badge augmentation enqueue failed (${response.status})`), response.status);
  }
}
